import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTitle } from '../../context/TitleContext';
import orderRepository from '../../services/repositories/order.repository';
import { getOrderTotals, getItemLineTotal, formatOrderPrice } from '../../utils/orderTotals';
import '../../styles/products.scss';

// Status configuration
const STATUS_CONFIG = {
  pending: { label: 'En attente', step: 0 },
  processing: { label: 'En traitement', step: 1 },
  shipped: { label: 'Expédié', step: 2 },
  delivered: { label: 'Livré', step: 3 },
  cancelled: { label: 'Annulé', step: -1 },
};

const PAYMENT_STATUS_CONFIG = {
  pending: { label: 'En attente', class: 'bg-warning' },
  paid: { label: 'Payé', class: 'bg-success' },
  failed: { label: 'Échoué', class: 'bg-danger' },
  refunded: { label: 'Remboursé', class: 'bg-info' },
};

const OrderShow = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { setTitle } = useTitle();
  const printRef = useRef(null);
  
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  // Fetch order data
  const fetchOrder = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await orderRepository.getById(id);
      const orderData = response.data || response;
      
      if (!orderData || !orderData.id) {
        throw new Error('Commande non trouvée');
      }
      
      setOrder(orderData);
      setTitle(`Commande #${orderData.serial_name || orderData.id}`);
    } catch (err) {
      console.error('Error fetching order:', err);
      setError(err.message || 'Erreur lors du chargement de la commande');
    } finally {
      setLoading(false);
    }
  }, [id, setTitle]);

  useEffect(() => {
    fetchOrder();
  }, [fetchOrder]);

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Format price helper
  const formatPrice = (price) => formatOrderPrice(price);

  // Get tracking steps
  const getTrackingSteps = () => {
    const steps = [
      { title: 'Commande confirmée', icon: 'ph-fill ph-check-circle' },
      { title: 'En préparation', icon: 'ph-fill ph-package' },
      { title: 'Expédiée', icon: 'ph-fill ph-truck' },
      { title: 'Livrée', icon: 'ph-fill ph-house' },
    ];

    const currentStep = STATUS_CONFIG[order?.status]?.step ?? 0;
    const orderDate = new Date(order?.created_at);

    return steps.map((step, index) => {
      const isCompleted = index <= currentStep && currentStep >= 0;
      let date = 'En attente';
      
      if (isCompleted && order?.created_at) {
        const stepDate = new Date(orderDate);
        stepDate.setDate(stepDate.getDate() + index);
        date = formatDateTime(stepDate);
      }

      return { ...step, completed: isCompleted, date };
    });
  };

  // Download PDF function
  const handleDownloadPDF = async () => {
    setDownloading(true);
    
    try {
      const html2canvasModule = await import('html2canvas');
      const jsPDFModule = await import('jspdf');
      
      const html2canvas = html2canvasModule.default;
      const jsPDF = jsPDFModule.default;
      
      const element = printRef.current;
      if (!element) {
        throw new Error('Element not found');
      }

      // Wait for images to load
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        allowTaint: true,
        foreignObjectRendering: false,
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;
      
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      
      pdf.save(`commande-${order?.serial_name || order?.id}.pdf`);
    } catch (err) {
      console.error('Error generating PDF:', err);
      // Fallback: print
      window.print();
    } finally {
      setDownloading(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <section className="py-80">
        <div className="container container-lg">
          <div className="text-center py-80">
            <i className="ph ph-spinner-gap animate-spin text-5xl text-main-600 mb-16" style={{ display: 'inline-block', animation: 'spin 1s linear infinite' }} />
            <p className="text-gray-600">Chargement de la commande...</p>
          </div>
        </div>
      </section>
    );
  }

  // Error state
  if (error) {
    return (
      <section className="py-80">
        <div className="container container-lg">
          <div className="text-center py-80">
            <i className="ph ph-warning-circle text-5xl text-danger mb-16" />
            <h5 className="text-danger mb-16">{error}</h5>
            <button 
              onClick={() => navigate('/admin/orders/orders-list')}
              className="btn btn-main py-12 px-32 rounded-pill"
            >
              <i className="ph ph-arrow-left me-8" />
              Retour à la liste
            </button>
          </div>
        </div>
      </section>
    );
  }

  const trackingSteps = getTrackingSteps();
  const paymentStatus = PAYMENT_STATUS_CONFIG[order?.payment_status] || PAYMENT_STATUS_CONFIG.pending;
  const orderTotals = getOrderTotals(order);

  return (
    <section className="py-32">
      <div className="container container-lg">
        {/* Header Actions */}
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-16 mb-32">
          <button 
            onClick={() => navigate('/admin/orders/orders-list')}
            className="btn btn-outline-secondary d-flex align-items-center gap-8 py-12 px-24 rounded-8"
          >
            <i className="ph ph-arrow-left" />
            Retour
          </button>
          <div className="d-flex gap-12 flex-wrap">
            <button 
              onClick={() => navigate(`/admin/orders/order-edit/${id}`)}
              className="btn btn-outline-primary d-flex align-items-center gap-8 py-12 px-24 rounded-8"
            >
              <i className="ph ph-pencil-simple" />
              Modifier
            </button>
            <button 
              onClick={handleDownloadPDF}
              disabled={downloading}
              className="btn btn-main d-flex align-items-center gap-8 py-12 px-24 rounded-8"
              style={{ backgroundColor: '#2ecc71', border: 'none' }}
            >
              {downloading ? (
                <>
                  <i className="ph ph-spinner-gap" style={{ animation: 'spin 1s linear infinite' }} />
                  Génération...
                </>
              ) : (
                <>
                  <i className="ph ph-file-pdf" />
                  Télécharger PDF
                </>
              )}
            </button>
          </div>
        </div>

        {/* Printable Content */}
        <div ref={printRef} style={{ backgroundColor: '#fff', padding: '20px' }}>
              {/* Order Header */}
              <div style={{ backgroundColor: '#f8f9fa', borderRadius: '16px', padding: '32px', marginBottom: '32px' }}>
                <div className="d-flex justify-content-between align-items-start flex-wrap gap-16 mb-24 pb-24" style={{ borderBottom: '1px solid #e9ecef' }}>
                  <div>
                    <h4 className="mb-8">Commande #{order?.serial_name || order?.id}</h4>
                    <p className="text-sm text-muted mb-0">
                      <i className="ph ph-calendar me-8" />
                      Passée le {formatDate(order?.created_at)}
                    </p>
                  </div>
                  <div className="text-end">
                    <span className={`badge ${paymentStatus.class} py-8 px-24 rounded-pill text-white`}>
                      Paiement: {paymentStatus.label}
                    </span>
                  </div>
                </div>

                {/* Tracking Steps */}
                <div className="mb-32">
                  {trackingSteps.map((step, index) => (
                    <div
                      key={index}
                      className="d-flex gap-24 mb-24"
                      style={{ marginBottom: index === trackingSteps.length - 1 ? '0' : '24px' }}
                    >
                      <div className="position-relative">
                        <div
                          className="d-flex align-items-center justify-content-center rounded-circle"
                          style={{ 
                            width: '40px', 
                            height: '40px', 
                            minWidth: '40px',
                            backgroundColor: step.completed ? '#5c6bc0' : '#e9ecef',
                            color: step.completed ? '#fff' : '#6c757d'
                          }}
                        >
                          {step.completed ? (
                            <i className="ph-fill ph-check text-lg" />
                          ) : (
                            <i className="ph ph-circle text-lg" />
                          )}
                        </div>
                        {index < trackingSteps.length - 1 && (
                          <div
                            className="position-absolute"
                            style={{ 
                              top: '100%',
                              left: '50%',
                              transform: 'translateX(-50%)',
                              width: '2px', 
                              height: '40px',
                              backgroundColor: step.completed ? '#5c6bc0' : '#e9ecef'
                            }}
                          />
                        )}
                      </div>
                      <div className="flex-grow-1">
                        <h6 className="mb-4" style={{ color: step.completed ? '#212529' : '#6c757d' }}>
                          {step.title}
                        </h6>
                        <p className="text-sm text-muted mb-0">{step.date}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Status Badge */}
                <div style={{ backgroundColor: '#e8f5e9', borderRadius: '8px', padding: '16px' }} className="d-flex align-items-center gap-12">
                  <i className="ph-fill ph-package text-2xl" style={{ color: '#4caf50' }} />
                  <div>
                    <p className="text-sm mb-0">
                      <strong>Statut actuel:</strong> {STATUS_CONFIG[order?.status]?.label || order?.status}
                    </p>
                  </div>
                </div>
              </div>

              {/* Customer & Delivery Info */}
              <div className="row mb-32">
                <div className="col-md-6 mb-24 mb-md-0">
                  <div style={{ border: '1px solid #e9ecef', borderRadius: '16px', padding: '24px', height: '100%' }}>
                    <h6 className="mb-16 pb-16 d-flex align-items-center gap-8" style={{ borderBottom: '1px solid #e9ecef' }}>
                      <i className="ph ph-user" style={{ color: '#5c6bc0' }} />
                      Informations Client
                    </h6>
                    <div>
                      <div className="d-flex justify-content-between mb-12">
                        <span className="text-muted">Nom:</span>
                        <span className="fw-semibold">{order?.user?.name || '-'}</span>
                      </div>
                      <div className="d-flex justify-content-between mb-12">
                        <span className="text-muted">Email:</span>
                        <span className="fw-semibold">{order?.user?.email || '-'}</span>
                      </div>
                      <div className="d-flex justify-content-between">
                        <span className="text-muted">Téléphone:</span>
                        <span className="fw-semibold">{order?.user?.phone || '-'}</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div style={{ border: '1px solid #e9ecef', borderRadius: '16px', padding: '24px', height: '100%' }}>
                    <h6 className="mb-16 pb-16 d-flex align-items-center gap-8" style={{ borderBottom: '1px solid #e9ecef' }}>
                      <i className="ph ph-map-pin" style={{ color: '#5c6bc0' }} />
                      Adresse de Livraison
                    </h6>
                    <p className="text-muted mb-0">
                      {order?.delivery_address || 'Aucune adresse spécifiée'}
                    </p>
                    {order?.delivery_latitude && order?.delivery_longitude && (
                      <p className="text-sm text-muted mt-8 mb-0">
                        <i className="ph ph-map-trifold me-4" />
                        {order.delivery_latitude}, {order.delivery_longitude}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Product Details */}
              <div style={{ border: '1px solid #e9ecef', borderRadius: '16px', padding: '32px', marginBottom: '32px' }}>
                <h6 className="mb-24 pb-24 d-flex align-items-center gap-8" style={{ borderBottom: '1px solid #e9ecef' }}>
                  <i className="ph ph-shopping-bag" style={{ color: '#5c6bc0' }} />
                  Détails du Produit
                </h6>
                
                {/* Check if order has items (checkout order) or single product */}
                {order?.items && order.items.length > 0 ? (
                  // Multiple items from checkout
                  <>
                    {order.items.map((item, index) => (
                      <div key={item.id || index} className="d-flex align-items-center gap-16 mb-16 pb-16" style={{ borderBottom: index < order.items.length - 1 ? '1px solid #e9ecef' : 'none' }}>
                        {item.product?.image ? (
                          <img 
                            src={item.product.image.startsWith('http') ? item.product.image : `/storage/${item.product.image}`}
                            alt={item.product_name || item.product?.name}
                            style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '8px' }}
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        ) : (
                          <div className="d-flex align-items-center justify-content-center" style={{ width: '60px', height: '60px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                            <i className="ph ph-image text-2xl text-muted" />
                          </div>
                        )}
                        <div className="flex-grow-1">
                          <h6 className="mb-4" style={{ fontSize: '14px' }}>{item.product_name || item.product?.name || '-'}</h6>
                          <p className="text-sm text-muted mb-0">
                            Quantité: {item.quantity || 1} × {formatPrice(item.price)}
                          </p>
                        </div>
                        <div className="text-end">
                          <p className="mb-0 fw-semibold" style={{ color: '#5c6bc0' }}>{formatPrice(getItemLineTotal(item))}</p>
                        </div>
                      </div>
                    ))}
                    {/* Order Summary for items */}
                    <div className="mt-24 pt-24" style={{ borderTop: '1px solid #e9ecef' }}>
                      <div className="d-flex justify-content-between align-items-center mb-8">
                        <span className="text-muted">Sous-total</span>
                        <span className="fw-medium">{formatPrice(orderTotals.subtotal)}</span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center mb-8">
                        <span className="text-muted">TVA (20%)</span>
                        <span className="fw-medium">{formatPrice(orderTotals.tax)}</span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center mb-16">
                        <span className="text-muted">Livraison</span>
                        <span className="fw-medium">{formatPrice(orderTotals.shipping)}</span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">Total</h5>
                        <h4 className="mb-0" style={{ color: '#5c6bc0' }}>{formatPrice(orderTotals.grandTotal)}</h4>
                      </div>
                    </div>
                  </>
                ) : order?.product ? (
                  // Single product order
                  <>
                    <div className="d-flex align-items-center gap-16 mb-24 pb-24" style={{ borderBottom: '1px solid #e9ecef' }}>
                      {order.product.image ? (
                        <img 
                          src={order.product.image.startsWith('http') ? order.product.image : `/storage/${order.product.image}`}
                          alt={order.product.name}
                          style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px' }}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      ) : (
                        <div className="d-flex align-items-center justify-content-center" style={{ width: '80px', height: '80px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                          <i className="ph ph-image text-3xl text-muted" />
                        </div>
                      )}
                      <div className="flex-grow-1">
                        <h6 className="mb-4">{order.product.name || '-'}</h6>
                        <p className="text-sm text-muted mb-0">SKU: {order.product.sku || '-'}</p>
                        {order.product.category && (
                          <span className="badge bg-light text-dark mt-8">{order.product.category.name}</span>
                        )}
                      </div>
                      <div className="text-end">
                        <h5 className="mb-0" style={{ color: '#5c6bc0' }}>{formatPrice(order.product.price)}</h5>
                      </div>
                    </div>
                    {/* Order Summary */}
                    <div className="mt-24 pt-24" style={{ borderTop: '1px solid #e9ecef' }}>
                      <div className="d-flex justify-content-between align-items-center mb-8">
                        <span className="text-muted">Sous-total</span>
                        <span className="fw-medium">{formatPrice(orderTotals.subtotal || order.product.price)}</span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center mb-8">
                        <span className="text-muted">TVA (20%)</span>
                        <span className="fw-medium">{formatPrice(orderTotals.tax || (parseFloat(order.product.price || 0) * 0.2))}</span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center mb-16">
                        <span className="text-muted">Livraison</span>
                        <span className="fw-medium">{formatPrice(orderTotals.shipping)}</span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">Total</h5>
                        <h4 className="mb-0" style={{ color: '#5c6bc0' }}>
                          {formatPrice(
                            orderTotals.grandTotal ||
                            (parseFloat(order.product.price || 0) * 1.2)
                          )}
                        </h4>
                      </div>
                    </div>
                  </>
                ) : (
                  // No product data
                  <div className="text-center py-24">
                    <i className="ph ph-package text-4xl text-muted mb-12" />
                    <p className="text-muted mb-0">Aucun produit associé à cette commande</p>
                  </div>
                )}
              </div>

              {/* Notes */}
              {order?.note && (
                <div style={{ border: '1px solid #e9ecef', borderRadius: '16px', padding: '32px', marginBottom: '32px' }}>
                  <h6 className="mb-16 pb-16 d-flex align-items-center gap-8" style={{ borderBottom: '1px solid #e9ecef' }}>
                    <i className="ph ph-note" style={{ color: '#5c6bc0' }} />
                    Notes
                  </h6>
                  <p className="text-muted mb-0">{order.note}</p>
                </div>
              )}

              {/* Footer Info */}
              <div style={{ backgroundColor: '#f8f9fa', borderRadius: '16px', padding: '24px', textAlign: 'center' }}>
                <p className="text-muted mb-8">
                  <i className="ph ph-clock me-8" />
                  Dernière mise à jour: {formatDateTime(order?.updated_at)}
                </p>
                <p className="mb-0">
                  Besoin d'aide? <Link to="/contact" style={{ color: '#5c6bc0', fontWeight: '600' }}>Contactez le support</Link>
                </p>
              </div>
        </div>
      </div>

      {/* Print styles */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @media print {
          .btn { display: none !important; }
          section { padding: 0 !important; }
        }
      `}</style>
    </section>
  );
};

export default OrderShow;
